// Per-week and whole-rotation parenting-time distribution.
//
// Answers "how many hours does each parent have?" — for each displayed week
// row and across the full rotation cycle. Parenting-time percentages are
// legally significant (e.g. the 40% shared-custody threshold in s.9 of the
// Federal Child Support Guidelines), so the math must mirror exactly what
// the calendar shows and what resolveRotationParent resolves.
//
// Week buckets are DISPLAYED rows (Sun-first): row N covers the previous
// data week's Sunday through data week N's Saturday — the same 7-day window
// the week grid draws. Cycle totals are identical under either convention;
// bucketing by displayed row keeps each week's numbers consistent with the
// grid the user reads them against.
//
// Overlapping events never double-count: the first matching event in
// week/event order owns the minutes, mirroring resolveRotationParent.

import { DAY_INDEX, MIN_PER_DAY, MIN_PER_WEEK, timeToMin } from './weekMath.js'

// "4d 6h", "3h 30m", "45m", "0h". Compact but unambiguous.
export function formatDurationShort(mins) {
  const d = Math.floor(mins / MIN_PER_DAY)
  const h = Math.floor((mins % MIN_PER_DAY) / 60)
  const m = Math.round(mins % 60)
  const parts = []
  if (d) parts.push(`${d}d`)
  if (h) parts.push(`${h}h`)
  if (m) parts.push(`${m}m`)
  return parts.join(' ') || '0h'
}

// Percentage with one decimal, trailing ".0" stripped: 57.1, 50, 42.9.
export function formatPercent(mins, baseMins) {
  if (!baseMins) return '0'
  const pct = (mins / baseMins) * 100
  const rounded = Math.round(pct * 10) / 10
  return String(rounded)
}

// → {
//     perWeek: [ { A, B, unassigned } ],   // minutes per displayed row
//     total:   { A, B, unassigned },       // minutes across the cycle
//     cycleMins,
//   }
export function computeTimeDistribution(rotation) {
  const weeks = rotation?.weeks || []
  const N = weeks.length
  const cycleMins = N * MIN_PER_WEEK
  const perWeek = Array.from({ length: N }, () => ({ A: 0, B: 0, unassigned: 0 }))
  const total = { A: 0, B: 0, unassigned: 0 }
  if (N === 0) return { perWeek, total, cycleMins }

  // Flatten events to [start, end) intervals on the cycle timeline, in the
  // same order resolveRotationParent scans them (week order, event order) so
  // first-match priority is identical. Wrapping events contribute two pieces.
  const intervals = []
  weeks.forEach((week, wi) => {
    ;(week?.events || []).forEach((e) => {
      const sOff = e.startWeekOffset || 0
      const eOff = e.endWeekOffset || 0
      let start = (wi + sOff) * MIN_PER_WEEK + (DAY_INDEX[e.startDay] ?? 0) * MIN_PER_DAY + timeToMin(e.startTime)
      let end = (wi + eOff) * MIN_PER_WEEK + (DAY_INDEX[e.endDay] ?? 0) * MIN_PER_DAY + timeToMin(e.endTime)
      if (end <= start) end += MIN_PER_WEEK // safety, matches rotation.js
      const shift = Math.floor(start / cycleMins) * cycleMins
      start -= shift
      end -= shift
      if (end > cycleMins) {
        intervals.push({ start, end: cycleMins, partner: e.partner })
        intervals.push({ start: 0, end: end - cycleMins, partner: e.partner })
      } else {
        intervals.push({ start, end, partner: e.partner })
      }
    })
  })

  // Split the cycle at every interval edge and displayed-row boundary, then
  // attribute each homogeneous segment to its owner (first matching interval).
  const points = new Set([0, cycleMins])
  intervals.forEach((iv) => { points.add(iv.start); points.add(iv.end) })
  for (let w = 0; w < N; w++) {
    points.add((w * MIN_PER_WEEK - MIN_PER_DAY + cycleMins) % cycleMins) // row w starts at its leading Sunday
  }
  const sorted = [...points].sort((a, b) => a - b)

  for (let i = 0; i < sorted.length - 1; i++) {
    const s = sorted[i]
    const e = sorted[i + 1]
    if (e <= s) continue
    const owner = intervals.find((iv) => s >= iv.start && s < iv.end)?.partner || null
    const row = Math.floor(((s + MIN_PER_DAY) % cycleMins) / MIN_PER_WEEK)
    const dur = e - s
    const key = owner === 'A' ? 'A' : owner === 'B' ? 'B' : 'unassigned'
    perWeek[row][key] += dur
    total[key] += dur
  }

  return { perWeek, total, cycleMins }
}
